import { useEffect, useState } from 'react'
import { useUserAuthSessionStore, useAppHydrationStore } from '@/store'
import { useLoadDisplayCurrency } from '@/hooks/useLoadDisplayCurrency'
import { debugUtils } from '@/config/app'

/**
 * Component for session initialization on app load
 * Restores session from localStorage if tokens exist
 *
 * Also signals to the centralized hydration store when auth is ready,
 * preventing flash of signup page on reload for authenticated users.
 */
export const SessionInitializer = () => {
  const restoreSession = useUserAuthSessionStore(
    (state) => state.restoreSession,
  )
  const setAuthSessionRestored = useAppHydrationStore(
    (state) => state.setAuthSessionRestored,
  )
  const [isInitialized, setIsInitialized] = useState(false)

  // Sync display currency (server as source of truth)
  // This must happen inside QueryClientProvider, hence placed here
  useLoadDisplayCurrency()

  useEffect(() => {
    const initializeSession = async () => {
      try {
        debugUtils.authLog(
          '🚀 SessionInitializer: Starting session restoration...',
        )

        // Restore session on app load
        await restoreSession()

        debugUtils.authLog(
          '✅ SessionInitializer: Session restoration completed',
        )
        setIsInitialized(true)
        // Signal to centralized hydration store
        setAuthSessionRestored()
      } catch (error) {
        setIsInitialized(true) // Mark as initialized regardless
        // Still signal hydration complete even on error
        setAuthSessionRestored()
      }
    }

    initializeSession()
  }, [restoreSession, setAuthSessionRestored])

  // Can add a loading indicator if needed
  if (!isInitialized) {
    // While session is being restored, can show something or just null
    return null
  }

  return null // This component renders nothing
}
